// Design: "EDIT: KYC Enhancements" (Fun Mobile V2 WIP), nodes 1508:2616 /
// 1631:7256 / 1631:7883.
//
// Colours live in `theme/tokens.ts` with every other one, so a brand override
// reaches them and `darkTheme()` gets the dark set. Only the geometry is here.

/**
 * Geometry read off the design frames. Kept together so a redraw is one edit
 * rather than a hunt through two components.
 */
export const KYC_FORM_METRICS = {
  /** Field height, and the back button's diameter. */
  fieldHeight: 56,
  fieldRadius: 16,
  fieldPaddingHorizontal: 16,
  /** An optional field's caption needs the extra 4px, so it insets less. */
  optionalPaddingHorizontal: 12,
  /** Where that caption sits, clear of the placeholder above it. */
  optionalCaptionBottom: 7,
  /** Between the rows of one section. */
  rowGap: 8,
  /*
   * The round's own error, drawn as a notice under the fields (1898:3254):
   * taller and rounder than a field, and set well clear of the last row.
   */
  noticeHeight: 60,
  noticeRadius: 26,
  /** Card bottom (364) to the notice (411). */
  noticeTop: 47,
  noticeTracking: -0.14,
  /*
   * The questionnaire pages (2475:8992), which draw their answers on the page
   * rather than behind a chooser. Rounder and tighter than a form field: the
   * card is one list, not a stack of boxes.
   */
  choiceListRadius: 26,
  choiceRowHeight: 52,
  choiceRowPaddingHorizontal: 20,
  /** Makes 52 on one line and the design's 64 on two — see `FiatChoiceList`. */
  choiceRowPaddingVertical: 15,
  choiceLabelFontSize: 15,
  choiceLabelLineHeight: 16.8,
  choiceCheckSize: 21,
  /** The title's own line, which these pages carry instead of two. */
  questionnaireTitleFontSize: 24,
  questionnaireTitleLineHeight: 26.88,
  questionnaireTitleTracking: -0.48,
  /** Below the back disc (108) to the title (162), as every `headingTop` is. */
  questionnaireHeadingTop: 54,
  /** Title to card (18), less the column's own gap. */
  questionnaireHeadingBottom: 10,
  /*
   * The date of birth page (2017:20247). Its title is the questionnaire's
   * size, set on a 203 measure — that is what breaks it over two lines — and
   * the wheel is centred on the frame rather than left under the heading.
   */
  dateTitleWidth: 203,
  dateWheelHeight: 216,
  /** Below the title, before the wheel. */
  dateHeadingBottom: 19,
  /** Between two fields sharing a row (first name + M.I., day/month/year). */
  fieldGap: 9,
  /** The middle-initial field is fixed while its neighbour grows. */
  initialWidth: 104,
  screenInset: 24,
  backButtonSize: 44,
  /** The disc clears the status bar by this much. */
  backButtonTop: 5,
  /** The heading is inset past the fields, not aligned to them. */
  headingInset: 40,
  /** Below the back disc. */
  headingTop: 32,
  /** Between the heading and the paragraph under it. */
  headingGap: 8,
  /** Below the paragraph, before the first field. */
  headingBottom: 16,
  /** The paragraph: larger than body, and the only place this size is used. */
  detailFontSize: 21,
  detailLineHeight: 24,
  /** KYC R2 actions clear the keyboard by 24 points (1951:8864). */
  advanceGap: 24,
  /**
   * The date picker's slot, taking the keyboard's place (1844:17543). Inset
   * because the design floats the calendar off the screen's edges.
   */
  datePickerHeight: 371,
  datePickerInsetX: 10,
  datePickerInsetY: 12,
  /** Tighter than over a keyboard: 1844:17471 puts the advance 24 above it. */
  datePickerGap: 24,
  /*
   * The intro screen (1844:17377), read off the 393x852 frame. Its headline is
   * top-anchored under the disc; the legal lines and the action hold the
   * bottom edge.
   */
  /** Disc bottom (108) to the headline's line box (181). */
  introHeadingTop: 73,
  /** 38/38 with the box 286 wide, which is what wraps it onto two lines. */
  introTitleFontSize: 38,
  introTitleTracking: -1.14,
  introTitleWidth: 286,
  /** Headline bottom (257) to the paragraph (276). */
  introTitleGap: 19,
  /** The paragraph's own measure (268), four lines of it. */
  introDetailWidth: 268,
  introDetailTracking: -0.42,
  /**
   * Below the action (784) to the frame's foot: the watermark's own 16 band,
   * its 18 of type, and the 34 the design clears the action by.
   */
  introActionBottom: 68,
  /** The consent checkbox, and the gap to the copy beside it. */
  consentBoxSize: 32,
  consentBoxGap: 10,
  /** Smaller than any body token, and used on the legal lines alone. */
  consentFontSize: 12,
  /** Four lines of it fill the 52 the frame gives the consent row. */
  consentLineHeight: 13,
  /** Between each legal line and the action below them. */
  consentGap: 16,
  /** They are inset past the action, as the heading is past the fields. */
  consentInset: 16,
  /*
   * The code screen's headline (2475:3268): larger, tighter and set in the
   * primary colour, unlike the KYC paragraph it used to borrow.
   */
  otpHeadingFontSize: 24,
  otpHeadingLineHeight: 27,
  otpHeadingTracking: -0.24,
  /** A code box is taller than a form field, and its digit larger. */
  codeBoxHeight: 64,
  codeBoxGap: 8,
  codeDigitFontSize: 28,
} as const
